import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { aboutPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll(".reveal").forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}

export default function About() {
  const revealRef = useRevealOnScroll();

  return (
    <>
      <SEO
        title="關於時光逆行駭客｜曾驛翔院長｜品牌故事與核心理念"
        description="深入了解時光逆行駭客的品牌故事與核心理念。曾驛翔院長以皮膚科背景結合醫學美容與功能性醫療，為不同年齡族群設計專屬逆齡路線圖。老化程式除錯，讀懂身體狀態。"
        keywords="關於時光逆行駭客,曾驛翔院長,品牌故事,醫學美容,功能性醫療,逆齡"
        canonicalPath="/about"
        ogType="profile"
        schema={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "關於時光逆行駭客｜曾驛翔院長",
          "description": "深入了解時光逆行駭客的品牌故事與核心理念。曾驛翔院長以皮膚科背景結合醫學美容與功能性醫療。",
          "about": {
            "@type": "Person",
            "name": "曾驛翔",
            "jobTitle": "院長",
            "description": "皮膚科背景｜醫學美容・功能性醫療整合｜時光逆行駭客",
          },
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg" ref={revealRef}>
      {/* ===== HERO ===== */}
      <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 circuit-bg opacity-20" />
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center max-w-4xl mx-auto">
            <span className="section-label reveal opacity-0 translate-y-6 transition-all duration-700">
              {aboutPageData.heroTagline}
            </span>
            <h1 className="mt-5 text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-ghost reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
              關於<span className="text-gradient-gold">時光逆行駭客</span>
              <br />
              <span className="text-[1.5rem] md:text-[2.125rem] font-semibold text-ghost-muted mt-2 block">
                曾驛翔院長
              </span>
            </h1>
            <p className="mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75] reveal opacity-0 translate-y-6 transition-all duration-700 delay-200">
              {aboutPageData.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* ===== WHAT IS CHRONOHACKER ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{aboutPageData.whatIs.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              時光逆行駭客是什麼？
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {aboutPageData.whatIs.cards.map((card, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center shrink-0">
                    <i className={`${idx === 0 ? "ri-bug-line" : "ri-dashboard-3-line"} text-cyber-gold text-xl`} />
                  </div>
                  <div>
                    <h3 className="text-[1.25rem] leading-[1.5] md:text-[1.625rem] md:leading-[1.45] font-semibold text-ghost mb-3">
                      {card.title}
                    </h3>
                    <p className="text-ghost-muted text-[1.0625rem] leading-[1.7]">{card.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== THREE ACTION RULES ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{aboutPageData.rules.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {aboutPageData.rules.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {aboutPageData.rules.items.map((rule, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-12 h-12 rounded-xl bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center font-mono text-cyber-gold text-[1.25rem] font-bold">
                    {rule.num}
                  </span>
                  <span className="font-mono text-neon-cyan text-base tracking-wider">
                    RULE #{rule.num}
                  </span>
                </div>
                <h3 className="text-[1.25rem] leading-[1.5] md:text-[1.625rem] md:leading-[1.45] font-semibold text-ghost mb-3">
                  {rule.title}
                </h3>
                <p className="text-ghost-muted text-[1.0625rem] leading-[1.7]">{rule.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROFILE CARD ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-5 reveal opacity-0 translate-y-6 transition-all duration-700">
              <div className="relative">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden border border-cyber-gold/20">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20male%20doctor%20portrait%20in%20dark%20futuristic%20medical%20environment%2C%20dramatic%20blue%20and%20gold%20lighting%2C%20confident%20expression%20in%20white%20coat%2C%20high-end%20medical%20brand%20photography%2C%20cinematic%20style%2C%20dark%20background%20with%20subtle%20tech%20elements%2C%20age%2040s%20Asian%20physician&width=600&height=800&seq=about-profile&orientation=portrait"
                    alt="曾驛翔院長"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 card-cyber px-5 py-4">
                  <p className="font-mono text-neon-cyan text-base">ROLE</p>
                  <p className="text-ghost font-bold text-[1.25rem] mt-1">時光逆行駭客</p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="section-label">{aboutPageData.profile.label}</span>
              <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
                {aboutPageData.profile.title}
              </h2>
              <p className="mt-2 text-cyber-gold text-[1.125rem] md:text-[1.25rem] font-medium">
                {aboutPageData.profile.subtitle}
              </p>
              <p className="mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75]">
                {aboutPageData.profile.description}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {aboutPageData.profile.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-lg text-base font-medium border border-cyber-gold/20 text-cyber-gold bg-cyber-gold/5"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Q&A ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{aboutPageData.qa.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {aboutPageData.qa.title}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto flex flex-col gap-5">
            {aboutPageData.qa.items.map((item, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-cyber-gold/10 border border-cyber-gold/20 flex items-center justify-center shrink-0">
                    <i className="ri-question-line text-cyber-gold text-lg" />
                  </div>
                  <div>
                    <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-ghost mb-2">
                      {item.q}
                    </h3>
                    <p className="text-ghost-muted text-[1.0625rem] leading-[1.7]">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="card-cyber p-10 md:p-16 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{aboutPageData.bottomCta.label}</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {aboutPageData.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-[1.0625rem]"
              >
                <i className="ri-line-fill" />
                {aboutPageData.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold text-[1.0625rem]">
                <i className="ri-calendar-check-line" />
                {aboutPageData.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}
